/**
 * report.ts — what the plays of a document add up to, for its author.
 *
 * A document's report is deliberately smaller than a book's. A book is read
 * end-to-end over sittings and its report is about the learner; a PDF is a deck of
 * pages that a class may each answer differently, and the question an author has is
 * narrower and more useful: *did anybody play it, how did they do, and which pages
 * catch people out*. A page that everybody misses is a page to re-teach or to
 * rewrite — that is the whole point of asking.
 *
 * Three decisions, inherited from the FlashBook report because they are right here
 * too:
 *
 *  - **Everything is derived.** The inputs are the raw plays and the material, so
 *    the numbers are recomputed rather than stored and a document edited after a
 *    play reports against what is on its pages now.
 *  - **A page is counted by what was answered, not by what it holds.** A learner
 *    who left a page unanswered is not a learner who got it wrong — the distinction
 *    is the difference between "this page is hard" and "this page is long".
 *  - **An empty report says so, in one number.** `plays: 0` is a fact, and a report
 *    that drew four zeroes and a 0% would read as failure rather than as silence.
 */
import {type AppBskyActorDefs} from '@atproto/api'

import {getSlideMaxPoints} from '#/lib/scoring'
import {type Slide} from '#/types/capsule'
import {type FlashBookPageResult} from '#/types/flashbook'

/** One play of a document, as the server lists it. */
export type DocumentPlay = {
  playerDid: string
  playerHandle?: string
  /**
   * Who played, as the server hydrated them.
   *
   * The plays route already answers with a profile view per player — name, face
   * and the *viewer's* relationship to them — so a card can draw who has played
   * without asking about anybody a second time. Only the two fields a report
   * cannot work without are required; a client that did not send a profile still
   * reports its play, against the DID alone.
   */
  player?: AppBskyActorDefs.ProfileViewBasic
  /** Points earned, when the play recorded a score. */
  score?: number
  /** Points on offer, when the play recorded them. */
  maxPoints?: number
  /**
   * How many pages of the document the read has been through.
   *
   * The *progress* half of a play, and the half no answer can stand in for: a
   * submission is the pages that asked something and were answered, and a document
   * is mostly pages that ask nothing — so the answers alone cannot tell an author
   * whether a reader stopped on page 4 or on page 24. The client sends this on every
   * Finish, counted from the pages it has brought into view, which is the same number
   * the card shows its own reader ("12 pages read").
   *
   * Optional because plays written before the field was on the wire carry only their
   * answers, and because a client that does not send it is still a client that played:
   * nothing may be counted from this alone (see `pagesRead` on the board).
   */
  pagesSeen?: number
  timeMs?: number
  pageResults: FlashBookPageResult[]
  createdAt?: string
}

/** One page, as its plays add up. */
export type DocumentPageReport = {
  /** 1-based page number. */
  page: number
  /** Questions the page holds today, from the material. */
  questions: number
  /** Answers given to this page, across every play. */
  answers: number
  /** Answers that were wrong. */
  misses: number
  /** Percent answered correctly, or `null` when nobody answered this page. */
  pct: number | null
}

export type DocumentReport = {
  /**
   * Submissions the server listed. A reader who works a second page submits
   * again, so this can exceed the number of people — see `players`.
   */
  plays: number
  /** Distinct learners, so one person replaying is not a class. */
  players: number
  /** Mean of each play's own percentage, or `null` when nothing scored. */
  averagePct: number | null
  /** Time spent, across the plays that reported it. */
  totalTimeMs: number
  /** The best percentage any one play reached, or `null`. */
  bestPct: number | null
  /** Every page the material covers, in order, with what its answers said. */
  pages: DocumentPageReport[]
  /**
   * The pages worth looking at again: those with misses, most missed first.
   * How many answers were *given* is the tie-break, so a page nobody reached is
   * never named ahead of one that was genuinely failed.
   */
  missedPages: DocumentPageReport[]
}

/**
 * A play's percentage, or `null` when it did not record one.
 *
 * Taken from the play's own score and maximum when they are there, because that is
 * what the server recorded at the time. When they are not — a play from a client
 * that did not send them — the answers are summed instead, using the *material's*
 * questions for the maxima, so the percentage is still about this document.
 */
function pctOfPlay(
  play: DocumentPlay,
  questionsById: Map<string, Slide>,
): number | null {
  if (
    typeof play.score === 'number' &&
    typeof play.maxPoints === 'number' &&
    play.maxPoints > 0
  ) {
    return Math.round((play.score / play.maxPoints) * 100)
  }
  let points = 0
  let maxPoints = 0
  for (const page of play.pageResults) {
    for (const block of page.blocks) {
      points += typeof block.points === 'number' ? block.points : 0
      const slide = questionsById.get(block.blockId)
      maxPoints += slide ? getSlideMaxPoints(slide) : 0
    }
  }
  return maxPoints > 0 ? Math.round((points / maxPoints) * 100) : null
}

/**
 * The newest submission from each reader.
 *
 * A submission is a reader's *whole* session, sent on every Finish, so a reader
 * who works a second page submits again — and the two rows describe one read at
 * two moments, not two reads. Counting both would double that reader's answers on
 * every page they had already done, keep a miss they had gone back and corrected,
 * and add a cumulative clock to itself. So the numbers below are computed from
 * each reader's latest submission, and the earlier ones are left where they are:
 * the count of submissions is still worth saying out loud, which is why `plays`
 * and `players` are two figures rather than one.
 *
 * The latest is the newest `createdAt`, falling back to the first in the list —
 * which is what the server lists first — for rows that arrived without one.
 */
function latestPerReader(plays: readonly DocumentPlay[]): DocumentPlay[] {
  const byReader = new Map<string, DocumentPlay>()
  for (const play of plays) {
    const seen = byReader.get(play.playerDid)
    if (!seen || (play.createdAt ?? '') > (seen.createdAt ?? '')) {
      byReader.set(play.playerDid, play)
    }
  }
  return [...byReader.values()]
}

/** The page number a result row belongs to, or `null` for one we cannot place. */
function pageNumberOf(result: FlashBookPageResult): number | null {
  const page = Number.parseInt(String(result.pageId), 10)
  return Number.isInteger(page) && page > 0 ? page : null
}

/**
 * A read in progress: the pages this reader has answered, in one set.
 *
 * A document is not read straight through like a book — a reader works the pages
 * that ask something, in whatever order they turn to them, and may come back to
 * one. So the play is not "the results of a sitting" but "what this reader has
 * answered so far", and every submission carries the whole of it.
 */
export type DocumentPlaySession = {
  /** Page results, one per page answered, replaced rather than appended. */
  results: FlashBookPageResult[]
  /**
   * Every page this read has brought into view, in the order it did — the last one
   * being the page the reader is on, and so the page they stopped on.
   *
   * Answering is not the same as reading: a document is mostly pages that ask
   * nothing, and a reader who gets to page 12 of 28 has *read* twelve pages whether
   * or not one of them asked them anything. Without this, a read is only ever
   * remembered as far as its answers went, and a reader who quits on a page with no
   * questions is offered the page their last *answer* was on — or nothing at all.
   */
  seenPages: number[]
  score: number
  maxPoints: number
  timeMs: number
  /** What each answered page's questions were worth, by page id. */
  pageMaxPoints?: Record<string, number>
}

/**
 * A read that has answered nothing yet.
 *
 * A shared constant rather than a fresh object per reader, because the first
 * submission's numbers are *derived from this*: a session merged onto an empty
 * one must equal the page that was merged into it, and that is only obviously
 * true when the starting point is visibly zero.
 */
export const EMPTY_PLAY_SESSION: DocumentPlaySession = {
  results: [],
  seenPages: [],
  score: 0,
  maxPoints: 0,
  timeMs: 0,
}

/**
 * A page brought into view, folded into the read in progress.
 *
 * The page moves to the *end* rather than being kept where it was first seen: the
 * order is the reader's own path, and the last entry has to be where they are — a
 * reader who went back to page 2 and left the reader there stopped on page 2, and
 * a set that remembered the highest page instead would send them forward past what
 * they had gone back to look at.
 */
export function mergeSeenPageIntoSession(
  session: DocumentPlaySession,
  page: number,
): DocumentPlaySession {
  if (!Number.isInteger(page) || page < 1) return session
  if (session.seenPages[session.seenPages.length - 1] === page) return session
  return {
    ...session,
    seenPages: [...session.seenPages.filter(seen => seen !== page), page],
  }
}

/**
 * The pages a read has brought into view, as a set and in order.
 *
 * The set rather than the count, because a count cannot be carried across sittings:
 * a reader who read three pages yesterday and three pages today has read six only if
 * today's three are unioned with yesterday's — and adding the numbers gives six for a
 * reader who read the *same* three pages twice. This reader's repo keeps the pages
 * themselves for exactly that reason (`#/lib/document/progress`), and the count the
 * card shows is this set's size.
 *
 * The pages come from the path *and* from the answers, because either is proof of a
 * page having been seen: a reader who answered a page and closed the reader without
 * turning it (the door out of a page is where they left) has read that page whatever
 * their path says, and a session rebuilt from a submission has nothing but answers to
 * count.
 */
export function documentReadPages(session: DocumentPlaySession): number[] {
  const seen = new Set<number>(session.seenPages)
  for (const result of session.results) {
    const page = pageNumberOf(result)
    if (page !== null) seen.add(page)
  }
  return [...seen].sort((a, b) => a - b)
}

/**
 * What a read amounts to, in the two numbers a reader is shown.
 *
 * Both are *counts of distinct things*, which is why neither is a length of
 * anything on the session: the pages are counted from the pages seen, and the
 * questions from the answers given — a page answered twice replaces its answers
 * and contributes them once, exactly as the score does.
 */
export function documentReadCounts(session: DocumentPlaySession): {
  /** How many pages of the document this read has brought into view. */
  pagesSeen: number
  /** How many questions have been answered, across every page answered. */
  answered: number
} {
  let answered = 0
  for (const result of session.results) {
    answered += result.blocks.length
  }
  return {pagesSeen: documentReadPages(session).length, answered}
}

/**
 * How many pages a submission should carry as read.
 *
 * The sitting's own path unioned with the pages the reader's repo remembers, because
 * the two know different halves of one read: a sitting opens in the middle of a
 * document (it is where the reader carried on from), so its path is the pages of *this*
 * visit while the record holds the pages of every visit — and a submission carrying
 * only this sitting's path would tell the author's report that the reader had gone
 * *backwards*. A union rather than the larger of two counts, because the two sets can
 * be disjoint: nine pages yesterday and two today is eleven pages read, not nine.
 *
 * A count with no list beside it is still a floor — a record written before pages were
 * listed, or one whose list was trimmed, counted at least that many — which is why
 * `remembered.pagesSeen` is taken as well as the pages.
 *
 * The result is what the card's own callout shows its reader (`documentResume`), so the
 * board and the card cannot disagree about how far the same read has got.
 */
export function pagesReadForSubmission(
  session: DocumentPlaySession,
  remembered?: {pagesSeen?: number; pages?: readonly number[]} | null,
): number {
  const pages = new Set<number>(documentReadPages(session))
  for (const page of remembered?.pages ?? []) {
    if (Number.isInteger(page) && page > 0) pages.add(page)
  }
  return Math.max(pages.size, remembered?.pagesSeen ?? 0)
}

/**
 * The page a read stopped on, or `undefined` when it has seen none.
 *
 * The last page seen, because that is where the reader was when they closed it —
 * the end of their path rather than its far point (see
 * `mergeSeenPageIntoSession`).
 */
export function stoppedPageOf(
  session: DocumentPlaySession,
): number | undefined {
  const seen = session.seenPages
  return seen.length > 0 ? seen[seen.length - 1] : undefined
}

/**
 * A page's results folded into the read in progress.
 *
 * A page answered twice *replaces* its earlier answers rather than adding to them:
 * the learner changed their mind, and a report that counted both would mark them
 * wrong for an answer they corrected. Whole pages are replaced, so the totals move
 * by the difference — which is what keeps the score honest across a retry.
 */
export function mergePageIntoSession(
  session: DocumentPlaySession,
  page: {
    pageResults: FlashBookPageResult
    score: number
    maxPoints: number
  },
): DocumentPlaySession {
  const pageId = page.pageResults.pageId
  const previous = session.results.find(result => result.pageId === pageId)
  const results = session.results.filter(result => result.pageId !== pageId)
  results.push(page.pageResults)
  results.sort((a, b) =>
    a.pageId.localeCompare(b.pageId, undefined, {numeric: true}),
  )

  return {
    results,
    /*
     * The path is *not* touched here, and that is deliberate: an answer is proof of
     * having seen a page, but it is not proof of where the reader *is*. The path is
     * the reader's own route, in their own order (see `mergeSeenPageIntoSession`),
     * and appending an answered page to it would make the page they answered the last
     * page they were on — a reader who answered page 2 and read on to page 9 would be
     * remembered as having stopped on page 2. An answered page still counts as seen:
     * the count is taken from the path *and* the answers (`documentReadCounts`).
     */
    seenPages: session.seenPages,
    score: session.score - previousBlocksScore(previous) + page.score,
    maxPoints:
      session.maxPoints - previousMaxPoints(session, previous) + page.maxPoints,
    timeMs:
      session.timeMs -
      (previous?.timeSpentMs ?? 0) +
      page.pageResults.timeSpentMs,
    pageMaxPoints: {
      ...session.pageMaxPoints,
      [pageId]: page.maxPoints,
    },
  }
}

/**
 * What the answered page's own points were, so a retry can be subtracted.
 *
 * The *blocks* are asked rather than remembered: the results carry their own
 * points, so a page's contribution can be recomputed from what was stored instead
 * of from a second copy of the totals that could drift away from it.
 */
function previousBlocksScore(
  previous: FlashBookPageResult | undefined,
): number {
  return (previous?.blocks ?? []).reduce(
    (sum, block) => sum + (typeof block.points === 'number' ? block.points : 0),
    0,
  )
}

/**
 * The maximum the *earlier* answers on that page were worth.
 *
 * Recorded on the session as a per-page figure when the page was finished, because
 * the block results do not carry a maximum and re-deriving it would mean having the
 * page's questions to hand — which the caller may no longer have once it has moved
 * on. Kept beside the results and dropped when the page is replaced.
 */
function previousMaxPoints(
  session: DocumentPlaySession,
  previous: FlashBookPageResult | undefined,
): number {
  if (!previous) return 0
  return session.pageMaxPoints?.[previous.pageId] ?? 0
}

/**
 * The reader's own latest submission for a document, or `undefined`.
 *
 * A play is attributed to the DID that wrote it, and the plays route lists every
 * reader — so "how far did *I* get" is a question already answered by data the
 * card has asked for, and needs no request of its own.
 */
export function viewerPlayOf(
  plays: readonly DocumentPlay[],
  viewerDid: string | undefined,
): DocumentPlay | undefined {
  if (!viewerDid) return undefined
  return latestPerReader(plays).find(play => play.playerDid === viewerDid)
}

/**
 * The read in progress, rebuilt from a submission.
 *
 * A submission *is* a session — the client sends the whole of it on every Finish
 * — so carrying on where it stopped is reading it back, with two things the
 * records do not hold: `pageMaxPoints` is deliberately absent. A result carries
 * the points a reader earned and never the points on offer, so the caller fills
 * those in from the material at the moment the questions are at hand (see
 * `pageMaxPointsOf`), which is what keeps a re-answered page from inflating the
 * maximum it is measured against.
 *
 * The second is the *path*: a submission is the pages answered, so the pages seen
 * are put in as the pages answered — everything a submission can prove, and not a
 * page more. A caller that holds this read's place (`#/lib/document/progress`) is
 * where the rest of the path comes from.
 */
export function playSessionOf(play: DocumentPlay): DocumentPlaySession {
  let earned = 0
  for (const page of play.pageResults) {
    for (const block of page.blocks) {
      earned += typeof block.points === 'number' ? block.points : 0
    }
  }
  const seenPages: number[] = []
  for (const result of play.pageResults) {
    const page = pageNumberOf(result)
    if (page !== null && !seenPages.includes(page)) seenPages.push(page)
  }
  seenPages.sort((a, b) => a - b)
  return {
    results: [...play.pageResults],
    seenPages,
    score: play.score ?? earned,
    maxPoints: play.maxPoints ?? 0,
    timeMs: play.timeMs ?? 0,
  }
}

/**
 * What each page's questions are worth, keyed by page id.
 *
 * The one thing a session rebuilt from a play cannot derive for itself: a reader
 * who answers a page again has the page's earlier points *subtracted*, and the
 * subtraction needs the maximum that page carried — which only the questions know.
 */
export function pageMaxPointsOf(
  material?: {pages: {page: number; questions: Slide[]}[]} | null,
): Record<string, number> {
  const maxima: Record<string, number> = {}
  for (const page of material?.pages ?? []) {
    const max = page.questions.reduce(
      (sum, question) => sum + getSlideMaxPoints(question),
      0,
    )
    // A page of questions worth nothing can move no percentage, so it is not
    // given a row: every entry here is a page whose maximum would be *lost*.
    if (max > 0) maxima[String(page.page)] = max
  }
  return maxima
}

/** Where a reader's own read of a document left off. */
export type DocumentResume = {
  /**
   * The page to carry on at: the page this reader stopped on, when their own place
   * is known, and otherwise the first that asks something they have not finished.
   *
   * The stopping page is the truer of the two answers. A read is mostly pages that
   * ask nothing, and "the first page that asks something unfinished" can be a long
   * way *ahead* of what the reader has actually read — a reader who quit on page 4
   * of a document whose next question is on page 20 would be offered page 20, and
   * seventeen unread pages would be skipped to get there. A page that asks is only
   * an unfinished page if it is *behind* nothing: the reader who stopped past it
   * either answered it or turned the question card down, and either is finished
   * with it (see `#/lib/document/quizGate`).
   *
   * The asking-page rule is kept for the reads that have no place of their own —
   * submissions made before there was one to write — so that a resume is never
   * lost, only sharpened.
   */
  page: number
  /**
   * How much of the read is done, in the two numbers the card shows.
   *
   * Carried out of here rather than recomputed by the caller, because the caller
   * has the session but not the *place*: the answers are in the submission, and the
   * pages seen may only exist in the reader's own record.
   */
  counts: {pagesSeen: number; answered: number}
  /**
   * The read so far, ready to be carried on from.
   *
   * It is handed back rather than rebuilt by the caller for the reason the whole
   * feature exists: a reader who works one page in a sitting and one in the next
   * is *one* read, and a submission that carried only the new page would read as
   * the earlier answers having been taken back.
   */
  session: DocumentPlaySession
}

/**
 * Where this reader's own read left off — or `undefined` when they have none.
 *
 * The ways to be `undefined` are the ways there is nothing to resume: nobody is
 * signed in to attribute a read to; the document asks nothing at all; this reader
 * has never submitted anything; their answers are all on pages that no longer ask
 * anything (a page whose questions the author removed leaves nothing behind to
 * carry on from); or every page that asks something is already answered — a
 * finished read is not a resumed one, and the press starting it again from the top
 * is what a reader who finished it is asking for.
 *
 * A page counts as *finished* when the read holds at least as many answers for it
 * as the page holds questions today. Counts rather than ids, because the post's
 * page table is what the card has before anything is fetched, and a submission can
 * be older than the material it describes — a page whose questions were added
 * since is a page the reader has not done. A page left half-answered is therefore
 * where the read carries on, not a page to step over.
 *
 * `read` is this surface's own sitting, and it is *merged* with the submission rather
 * than chosen over it. Its answers win where it has any — it is the newest thing the
 * reader did, and the only record there is of a Finish whose upload failed or has
 * not been asked about again — but its *path* is the part no submission can hold,
 * and a reader who has turned ten pages and answered none of them has a read to
 * carry on all the same. Dropping the submission for the read would lose every
 * earlier sitting the moment a page was turned.
 *
 * `place` is where this reader's own read stopped, from the private record in their
 * repo (`#/lib/document/progress`), and it is the only thing here that knows about
 * the pages that asked *nothing* — every other input is a record of questions.
 *
 * `pageCount` is the document's length, from the post. It is what makes "finished"
 * mean something for a reader who has answered every question but has not read the
 * whole thing: a read that is done asking is not necessarily a document read to the
 * end, and the one that is not is a read to carry on with.
 */
export function documentResume({
  plays,
  viewerDid,
  pageTable,
  read,
  place,
  pageCount,
}: {
  plays: readonly DocumentPlay[]
  viewerDid: string | undefined
  /** What each page holds, off the post: a decision must not wait on a fetch. */
  pageTable?: {pages: {page: number; questions: number}[]} | null
  /** The read in progress on this surface, when it has one. */
  read?: DocumentPlaySession
  /**
   * Where this reader's read stopped, when their own repo has said so.
   *
   * `pagesSeen` and `answered` are taken on trust from the record only when there
   * is no submission to count instead: an answer that has been given since is a
   * fresher fact than a record of one.
   */
  place?: {page: number; pagesSeen: number; answered: number} | null
  /** The document's length in pages, when the caller knows it. */
  pageCount?: number
}): DocumentResume | undefined {
  if (!viewerDid) return undefined
  const asking = (pageTable?.pages ?? []).filter(row => row.questions > 0)
  if (asking.length === 0) return undefined

  const play = viewerPlayOf(plays, viewerDid)
  const submission = play ? playSessionOf(play) : undefined
  /*
   * The read in hand, and the two halves it can be made of.
   *
   * A sitting on this surface and a submission from an earlier one know different
   * things: the submission holds the answers of every sitting the server has heard
   * about, and the read in hand holds the pages turned in *this* one — including the
   * pages that asked nothing, which no submission carries. Neither may be dropped
   * for the other. A reader who has turned ten pages and answered none has a read to
   * carry on, and their earlier answers are still theirs; a reader who has answered
   * in this sitting has a read whose answers are the newer ones.
   */
  const session =
    read && submission
      ? {
          ...submission,
          seenPages:
            read.seenPages.length > 0 ? read.seenPages : submission.seenPages,
          ...(read.results.length > 0
            ? {
                results: read.results,
                score: read.score,
                maxPoints: read.maxPoints,
                timeMs: read.timeMs,
                pageMaxPoints: read.pageMaxPoints,
              }
            : {}),
        }
      : (read ?? submission)
  /*
   * Where the reader stopped: the read in hand's own path when it has one — it is
   * the newest thing they did — and otherwise the place in their repo, which is the
   * only thing that survives the reader being closed.
   */
  const stopped =
    (read ? stoppedPageOf(read) : undefined) ??
    (place && place.page > 0 ? place.page : undefined)
  if (!session && stopped === undefined) return undefined

  const answersByPage = new Map<number, number>()
  for (const result of session?.results ?? []) {
    const page = pageNumberOf(result)
    // A page answered twice is one row in a submission, but an older record can
    // still hold both: the later one wins, as it does everywhere else here.
    if (page !== null) answersByPage.set(page, result.blocks.length)
  }

  const finished = new Set<number>()
  let touchedSomething = false
  for (const row of asking) {
    const answers = answersByPage.get(row.page) ?? 0
    if (answers > 0) touchedSomething = true
    if (answers >= row.questions) finished.add(row.page)
  }
  /*
   * A place is something to carry on from even when no question has been answered:
   * a reader who read nine pages of a document whose first question is on page nine
   * has a read in progress, and the pull to it is the same pull.
   */
  if (!touchedSomething && stopped === undefined) return undefined

  /*
   * Where to carry on: the reader's own place when there is one, and the first
   * unfinished question otherwise. The place is clamped to the document, because a
   * document can lose pages after a place was written and a resume that named a
   * page that is no longer there would open past its end.
   */
  const next = asking.find(row => !finished.has(row.page))
  /** Whether the reader's own place is at, or past, the document's last page. */
  const atTheEnd =
    stopped !== undefined && pageCount !== undefined && stopped >= pageCount

  /*
   * With no page left to ask, the offer is the *rest of the document* — the pages
   * with nothing on them — and there is only a rest when the reader has one to
   * read. A reader who finished both halves (every question answered, last page
   * reached) has nothing to carry on with, and the press starting the document
   * again from the top is what they are asking for.
   */
  if (!next && (stopped === undefined || atTheEnd)) return undefined

  /*
   * With a question left, the place still wins *unless it ran off the end*: a page
   * that asks something can be added to a document whose reader had already read to
   * the last page, and the promise of a resume is the question, not the place.
   */
  const unclamped = stopped !== undefined && !atTheEnd ? stopped : next!.page
  const page = Math.min(
    unclamped,
    pageCount !== undefined && pageCount > 0
      ? pageCount
      : Number.MAX_SAFE_INTEGER,
  )

  const counts = documentReadCounts(session ?? EMPTY_PLAY_SESSION)
  return {
    page,
    counts: {
      /*
       * The read in hand is the newest word on both numbers, and the record fills
       * in what no submission can hold: a record can trail the answers (it is
       * written on the way past), and answers carry no path. So each number is the
       * larger of the two — never either-or, and never a number that moves
       * backwards because one of the two sources is older.
       */
      pagesSeen: Math.max(counts.pagesSeen, place?.pagesSeen ?? 0),
      answered: Math.max(counts.answered, place?.answered ?? 0),
    },
    session: session ?? EMPTY_PLAY_SESSION,
  }
}

/**
 * The answers an earlier sitting left, folded into the read in hand — or `null`.
 *
 * A sitting in this visit and a submission from an earlier one are *one* read, and
 * the sitting starts empty — so the first thing either surface does (turning a page,
 * answering one) adopts what the server already knows. What is *not* adopted is the
 * submission's idea of the path: the pages answered are all a submission can prove,
 * and this sitting's own path — including the pages that ask nothing — is the newer
 * truth about where the reader is (`mergeSeenPageIntoSession` adds those).
 *
 * The per-page maxima come off the material rather than the record, which is the
 * one thing a submission cannot carry (`pageMaxPointsOf`).
 *
 * `null` means "nothing to adopt", and it is the whole point of the function: a
 * resume that offers a *page* and carries no answers is an offer to carry on and
 * not a read to adopt, and a caller that writes its result into state would render
 * once per adopt. Such a resume — the one a reader's own place produces, which
 * never stops offering itself — would then render on every pass, which is the
 * update loop the reader crashed with. Adoption is one-shot by construction: it
 * happens only while the session holds no answers, and it leaves the session
 * holding them.
 */
export function adoptEarlierAnswers(
  session: DocumentPlaySession,
  resume: DocumentResume | undefined,
  material?: {pages: {page: number; questions: Slide[]}[]} | null,
): DocumentPlaySession | null {
  // A sitting that has answered something is already carrying a read of its own,
  // and the earlier answers belong to it either way — the surface adopts once, on
  // its way in.
  if (session.results.length > 0) return null
  if (!resume || resume.session.results.length === 0) return null
  return {
    ...session,
    results: resume.session.results,
    score: resume.session.score,
    maxPoints: resume.session.maxPoints,
    timeMs: resume.session.timeMs,
    pageMaxPoints: pageMaxPointsOf(material),
  }
}

/**
 * How many places count as "the top": the podium the card ranks, crowns and
 * lists in full before it starts skipping.
 */
export const LEADERBOARD_TOP = 3

/**
 * One reader's standing in a document's leaderboard.
 *
 * A *read*, not a submission: the entries are built from each reader's latest
 * submission, so a reader who works a second page is one line whose numbers have
 * moved, not two lines competing with each other.
 */
export type DocumentLeaderboardEntry = {
  /** 1-based place, best first. */
  rank: number
  playerDid: string
  /** The reader, as the server hydrated them. Never absent: the DID stands in. */
  profile: AppBskyActorDefs.ProfileViewBasic
  /** Handle, when one is known. */
  handle?: string
  /**
   * Percent of the answers this read submitted, or `null` when nothing scored.
   *
   * Percent rather than points, because a reader who answered one page out of
   * twenty-eight is not competing with one who answered all of them — see
   * `pctOfPlay`.
   */
  pct: number | null
  score: number
  maxPoints: number
  /** Questions this reader answered. */
  answers: number
  /**
   * How many pages of the document this read has been through.
   *
   * The play's own count when it carries one, floored by the pages it answered — an
   * answer is proof of having seen a page, and a play written before the field was on
   * the wire carries nothing else. This is the numerator of a read's progress and the
   * page number the dialog prints on the row.
   */
  pagesRead: number
  /**
   * What a read's progress is measured against, or `null` when nobody knows.
   *
   * The document's own length when the caller has it — the post carries it, and it is
   * what the card's "/ 16" chip and the reader's "carry on from page 12" mean by a page
   * count — and otherwise what the questions describe, which under-counts a document
   * whose last pages ask nothing. Null is not zero: a document of unknown length is one
   * nobody can be measured against, where a document that asks nothing has a length
   * every reader is equally far through — see `coverageOf` by way of `weighted`.
   */
  progressPages: number | null
  /**
   * Percent, weighted by how much of the document the read has been through —
   * `pagesRead` of `progressPages` — and the one number this board is ordered by.
   *
   * Score and progress *together*, because either one alone is a way to be wrong: on
   * percent alone, a reader who answered the first page perfectly outranks somebody
   * who has been through the whole deck; on pages alone, a reader who turned every
   * page and understood none of it outranks somebody who has answered half of them
   * and got them all right. `null` when nothing scored, which is a read that cannot
   * be placed at all; those are listed after everybody who scored (see below).
   */
  weighted: number | null
  timeMs: number
  createdAt?: string
  /** The viewer follows this reader. */
  youFollow: boolean
  /** This reader follows the viewer, which is how two people here become friends. */
  followsYou: boolean
  /** Whether this line is the viewer's own read. */
  isViewer: boolean
}

/**
 * Which readers the viewer knows, in either direction.
 *
 * "Knows" is deliberately the union of the two follow edges: a classmate who
 * follows you is as much *your* people as one you follow, and the card that says
 * so is trying to be encouraging rather than to draw a graph.
 */
export function isKnownToViewer(entry: DocumentLeaderboardEntry): boolean {
  return entry.youFollow || entry.followsYou
}

/**
 * What a read's progress is measured against, or `null` when nobody knows.
 *
 * The document's own length first: the post carries it (`pageCount`), and it is what
 * a reader already means by a page count — the card's "/ 16" chip, the reader's "page
 * 12 of 16", and the resume's "carry on from page 12" are all the same denominator.
 * Failing that, what the questions describe — the material's own pages when they have
 * been fetched, the page table when they have not — which under-counts a document
 * whose last pages ask nothing, but is the best a caller holding neither the post's
 * count nor the file can do.
 *
 * Null is not zero, and the difference is the whole reason this is not a length: a
 * document of unknown size is one nobody can be measured against, while a document
 * that asks nothing *has* a size — one every reader is equally far through — and
 * `coverageOf` treats the two differently.
 */
function progressPageCount({
  pageCount,
  material,
  pageTable,
}: {
  pageCount?: number
  material?: {pages: {page: number; questions: Slide[]}[]} | null
  pageTable?: {pages: {page: number; questions: number}[]} | null
}): number | null {
  if (typeof pageCount === 'number' && pageCount > 0) return pageCount
  if (material) return material.pages.length > 0 ? material.pages.length : null
  if (pageTable)
    return pageTable.pages.length > 0 ? pageTable.pages.length : null
  return null
}

/**
 * How much of the document a read has been through, 0–1.
 *
 * Held to 1 because the two sources can disagree: a post's page count can be older
 * than the plays beneath it (a page that stopped asking still carries answers on
 * them), and a reader who answered a page the table has since dropped is not at 120%
 * of the document. Unknown size gives 1, which leaves the percent alone as the key
 * (see the leaderboard comment below).
 */
function coverageOf({
  pagesRead,
  progressPages,
}: {
  pagesRead: number
  progressPages: number | null
}): number {
  if (progressPages === null || progressPages <= 0) return 1
  return Math.min(1, pagesRead / progressPages)
}

/**
 * A document's readers, best first.
 *
 * Four decisions worth stating, because each of them is a way to be wrong:
 *
 *  - **Percent *and* progress, as one key.** Neither alone says who is doing best:
 *    percent alone puts a reader who answered one page perfectly above somebody who
 *    has been through the whole deck, and pages alone puts a reader who turned every
 *    page and understood none of it above somebody who answered half of them and got
 *    them all right. So the key is `pct × coverage` — how well they did, scaled by how
 *    much of the document they did it on (`weighted`) — and the dialog prints both
 *    halves of it on the row, so the order and the reason for it are read together.
 *  - **Progress is the pages a read has been through, over the document's length.**
 *    Not the pages it *answered*: a document is mostly pages that ask nothing, and a
 *    reader who has read twenty of them has read twenty of them, whatever those pages
 *    asked. Not the *questions* either — a page is the unit a reader moves through and
 *    a page's questions are met on, so "twelve of sixteen pages" is progress a reader
 *    and an author both recognise, where counting questions would make a page holding
 *    one weigh a third of a page holding three, which is not how a document is read.
 *  - **A document of unknown length ranks on percent alone.** With no length and no
 *    material, the pages a read has been through are not a share of anything, and
 *    inventing a denominator would rank readers on a guess (`weighted` keeps the
 *    percent and takes coverage as 1). What a caller can nearly always give instead is
 *    the post's own page count — and failing that the *page table*, which the post
 *    carries and no fetch is needed for — see `pageCount` and `pageTable` below.
 *  - **A reader with no percentage is still a reader.** They are listed after
 *    everyone who scored, in the order they got there, rather than dropped: a play
 *    that cannot be compared is still somebody who played.
 *
 * Ties on the key are broken by how much work is behind it — more questions
 * answered, then faster, then earlier — so two readers the key cannot separate are
 * separated by something a reader can see.
 */
export function buildDocumentLeaderboard({
  plays,
  material,
  pageTable,
  pageCount,
  viewerDid,
}: {
  plays: readonly DocumentPlay[]
  /** The questions on each page, so a play without a score can be marked. */
  material?: {pages: {page: number; questions: Slide[]}[]} | null
  /**
   * What each page holds, when only the *counts* are known.
   *
   * The post carries this table (`materialPages`) and the composer builds it from
   * the sheet, so a card can rank its readers — and crown one of them — before a
   * single question has been fetched. Preferred in the opposite order to `material`
   * below, because a table that says a page asks two questions when the material
   * holds one is the stale one.
   */
  pageTable?: {pages: {page: number; questions: number}[]} | null
  /**
   * The document's own length in pages, when the caller knows it.
   *
   * The post carries it, so both the card and the author's dialog can have it without
   * fetching anything, and it is the denominator a reader recognises: "12 of 16 pages"
   * is the same thing the reader's own callout says when it offers to carry on from
   * page 12. Absent means "nobody knows how long this document is", which is not the
   * same answer as a short document (`progressPageCount`).
   */
  pageCount?: number
  /** Who is looking, so their own line can be marked and their ties named. */
  viewerDid?: string
}): DocumentLeaderboardEntry[] {
  const questionsById = new Map<string, Slide>()
  for (const page of material?.pages ?? []) {
    for (const question of page.questions) {
      questionsById.set(question.id, question)
    }
  }

  const progressPages = progressPageCount({pageCount, material, pageTable})

  const entries = latestPerReader(plays).map(play => {
    let answers = 0
    let answered = 0
    for (const result of play.pageResults) {
      const onPage = result.blocks.length
      if (onPage > 0) answered += 1
      answers += onPage
    }
    /*
     * How far this read got: what it says it read, floored by the pages it answered.
     * An answer is proof of having seen a page — and a play written before the count
     * was on the wire has nothing else to be counted by, which is what keeps an older
     * reader on the board rather than at nought.
     */
    const pagesRead = Math.max(play.pagesSeen ?? 0, answered)
    const profile: AppBskyActorDefs.ProfileViewBasic = play.player ?? {
      did: play.playerDid,
      handle: play.playerHandle ?? 'handle.invalid',
    }
    const pct = pctOfPlay(play, questionsById)
    return {
      playerDid: play.playerDid,
      profile,
      handle: profile.handle,
      pct,
      score: play.score ?? 0,
      maxPoints: play.maxPoints ?? 0,
      answers,
      pagesRead,
      progressPages,
      weighted:
        pct === null ? null : pct * coverageOf({pagesRead, progressPages}),
      timeMs: play.timeMs ?? 0,
      createdAt: play.createdAt,
      youFollow: !!profile.viewer?.following,
      followsYou: !!profile.viewer?.followedBy,
      isViewer: !!viewerDid && play.playerDid === viewerDid,
    }
  })

  entries.sort((a, b) => {
    const aKey = a.weighted ?? -1
    const bKey = b.weighted ?? -1
    if (aKey !== bKey) return bKey - aKey
    if (a.answers !== b.answers) return b.answers - a.answers
    // A reader who reported no clock is not "fastest": unrecorded sits last.
    const aTime = a.timeMs > 0 ? a.timeMs : Number.MAX_SAFE_INTEGER
    const bTime = b.timeMs > 0 ? b.timeMs : Number.MAX_SAFE_INTEGER
    if (aTime !== bTime) return aTime - bTime
    return (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
  })

  return entries.map((entry, index) => ({...entry, rank: index + 1}))
}

/** What the card draws about who has played, and what it names. */
export type DocumentPlayerProof = {
  /**
   * The faces to draw, in order: the viewer, then the people they know, then the
   * readers who arrived most recently.
   */
  players: DocumentLeaderboardEntry[]
  /** The people the viewer knows who placed in the top three — they wear the crown. */
  crowned: DocumentLeaderboardEntry[]
  /** Everyone the viewer knows who played, best placed first. */
  known: DocumentLeaderboardEntry[]
  /** Every reader who played — the number the line says out loud. */
  readers: number
  /** How many of them the line does not name. */
  others: number
  /** The viewer's own standing, when they have played. */
  viewer?: DocumentLeaderboardEntry
}

/** How many readers the strip says by name, at most. */
const NAMED_IN_COPY = 2

export function selectPlayerProof({
  leaderboard,
  top = LEADERBOARD_TOP,
  limit = 5,
}: {
  leaderboard: readonly DocumentLeaderboardEntry[]
  /** How many places count as "the top" for the crown. */
  top?: number
  /** How many faces fit in the strip. */
  limit?: number
}): DocumentPlayerProof | undefined {
  if (leaderboard.length === 0) return undefined

  const viewer = leaderboard.find(entry => entry.isViewer)
  const known = leaderboard.filter(
    entry => !entry.isViewer && isKnownToViewer(entry),
  )

  /*
   * The faces, in the order the reader cares about them: their own first (this is
   * about them playing, not only about other people), then the people they know,
   * and only then strangers — newest first, because the newest play is the one
   * that makes the document look alive.
   */
  const players: DocumentLeaderboardEntry[] = []
  const drawn = new Set<string>()
  const draw = (entry: DocumentLeaderboardEntry | undefined) => {
    if (!entry || players.length >= limit || drawn.has(entry.playerDid)) return
    drawn.add(entry.playerDid)
    players.push(entry)
  }
  draw(viewer)
  for (const entry of known) draw(entry)
  for (const entry of [...leaderboard].sort((a, b) =>
    (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
  )) {
    draw(entry)
  }

  return {
    players,
    crowned: known.filter(entry => entry.rank <= top),
    known,
    readers: leaderboard.length,
    others: Math.max(
      0,
      leaderboard.length - Math.min(known.length, NAMED_IN_COPY),
    ),
    viewer,
  }
}

/**
 * What the strip's one line should be about.
 *
 * The strip exists to give a reader a reason to press Play, and *who the line is
 * about* is the whole of the writing. Two readings, in the order they win:
 *
 *  - **A person the viewer knows, still ahead of them.** A friend's name with a
 *    place is the strongest thing the card can say — but only while they are in
 *    front. "Can you catch up?" asked of the reader who is *ahead* of them is a
 *    sentence nobody would say out loud, and a crowned friend the viewer has
 *    already overtaken is not a reason to press anything.
 *  - **The viewer's own standing**, once they have played. They are on the board
 *    already, and a line that counts them among the crowd — "4 readers have
 *    played, see where you land" — is answering a question they are not asking.
 *
 * A reader who has not played is the strip's original audience, and hears about
 * the others: a name when the strip knows one, the crowd when it does not.
 */
export type DocumentStripLead =
  | {kind: 'name'; entry: DocumentLeaderboardEntry}
  | {kind: 'you'; entry: DocumentLeaderboardEntry}
  | {kind: 'crowd'}

/** Which reading the strip's line takes. */
export function documentStripLead(
  proof: DocumentPlayerProof,
): DocumentStripLead {
  const {viewer, crowned} = proof
  if (viewer) {
    const ahead = crowned.find(entry => entry.rank < viewer.rank)
    return ahead ? {kind: 'name', entry: ahead} : {kind: 'you', entry: viewer}
  }
  return crowned[0] ? {kind: 'name', entry: crowned[0]} : {kind: 'crowd'}
}

/** One line of the leaderboard card: a reader, or the readers skipped over. */
export type DocumentLeaderboardRow =
  | {kind: 'entry'; entry: DocumentLeaderboardEntry}
  | {kind: 'gap'; count: number}

/**
 * The leaderboard as the card shows it: the top places, and — when the viewer is
 * not among them — their own line with its neighbours.
 *
 * "…21 *you* 23…" is the whole point: a reader who is 21st of 200 should see their
 * own place and the two people they are actually racing, not a wall of strangers
 * they will never pass.
 */
export function documentLeaderboardRows(
  leaderboard: readonly DocumentLeaderboardEntry[],
  {top = LEADERBOARD_TOP}: {top?: number} = {},
): DocumentLeaderboardRow[] {
  const head: DocumentLeaderboardRow[] = leaderboard
    .slice(0, top)
    .map(entry => ({kind: 'entry', entry}))
  if (leaderboard.length <= head.length) return head

  const viewerIndex = leaderboard.findIndex(entry => entry.isViewer)
  if (viewerIndex < top) {
    // Either there is no viewer here, or they are already on the podium: the
    // rest of the board is one gap away and nothing more needs saying.
    return [...head, {kind: 'gap', count: leaderboard.length - head.length}]
  }

  const from = Math.max(top, viewerIndex - 1)
  const to = Math.min(leaderboard.length, viewerIndex + 2)
  const before = from - top
  const after = leaderboard.length - to
  return [
    ...head,
    ...(before > 0 ? [{kind: 'gap' as const, count: before}] : []),
    ...leaderboard
      .slice(from, to)
      .map(entry => ({kind: 'entry' as const, entry})),
    ...(after > 0 ? [{kind: 'gap' as const, count: after}] : []),
  ]
}

/**
 * The author's summary of a document's plays.
 *
 * `material` is the document's material, so the report knows which pages were
 * asked anything at all: a document with no questions reports plays and nothing
 * else, rather than a page-by-page table of blanks.
 */
export function buildDocumentReport({
  plays,
  material,
}: {
  plays: readonly DocumentPlay[]
  /** The questions on each page, keyed by page number. */
  material?: {pages: {page: number; questions: Slide[]}[]} | null
}): DocumentReport {
  const questionsById = new Map<string, Slide>()
  const questionsByPage = new Map<number, number>()
  for (const page of material?.pages ?? []) {
    questionsByPage.set(page.page, page.questions.length)
    for (const question of page.questions) {
      questionsById.set(question.id, question)
    }
  }

  // Answers and misses per page, across each reader's latest submission.
  const stats = new Map<number, {answers: number; misses: number}>()
  const latest = latestPerReader(plays)
  let totalTimeMs = 0
  let bestPct: number | null = null
  const pcts: number[] = []

  for (const play of latest) {
    if (typeof play.timeMs === 'number' && play.timeMs > 0) {
      totalTimeMs += play.timeMs
    }
    for (const result of play.pageResults) {
      const page = pageNumberOf(result)
      if (page === null) continue
      const entry = stats.get(page) ?? {answers: 0, misses: 0}
      for (const block of result.blocks) {
        entry.answers += 1
        if (block.isCorrect === false) entry.misses += 1
      }
      stats.set(page, entry)
    }
    const pct = pctOfPlay(play, questionsById)
    if (pct !== null) {
      pcts.push(pct)
      bestPct = bestPct === null ? pct : Math.max(bestPct, pct)
    }
  }

  const pages: DocumentPageReport[] = [...questionsByPage.keys()]
    .sort((a, b) => a - b)
    .map(page => {
      const entry = stats.get(page)
      const answers = entry?.answers ?? 0
      const misses = entry?.misses ?? 0
      return {
        page,
        questions: questionsByPage.get(page) ?? 0,
        answers,
        misses,
        pct:
          answers > 0 ? Math.round(((answers - misses) / answers) * 100) : null,
      }
    })

  const missedPages = pages
    .filter(page => page.misses > 0)
    .sort((a, b) => b.misses - a.misses || b.answers - a.answers)

  return {
    plays: plays.length,
    players: latest.length,
    averagePct:
      pcts.length > 0
        ? Math.round(pcts.reduce((sum, pct) => sum + pct, 0) / pcts.length)
        : null,
    totalTimeMs,
    bestPct,
    pages,
    missedPages,
  }
}
